/**
 * REST-клиент обратной связи (/api/v1/feedback*, /api/v1/admin/feedback*).
 */

import { del, downloadFile, get, patch, post, postFormData } from "@/lib/api-client-v2";

export type ReportCategory = "bug" | "content" | "payment" | "other";
export type ReportStatus = "new" | "in_progress" | "resolved" | "closed";
export type ReportPriority = "low" | "medium" | "high" | "critical";

export type ReportMetadata = {
  url?: string;
  pathname?: string;
  referrer?: string | null;
  user_agent?: string;
  screen?: string;
  language?: string | null;
  platform?: string | null;
  ip?: string;
  [key: string]: unknown;
};

export type ErrorReportCreatePayload = {
  title: string;
  description: string;
  category: ReportCategory;
  app_version?: string;
  metadata?: ReportMetadata;
};

export type AttachmentOut = {
  id: number;
  report_id: number;
  file_name: string;
  file_size: number;
  mime_type: string;
  uploaded_at: string;
  download_url?: string | null;
};

export type CommentOut = {
  id: number;
  report_id: number;
  author_id?: string | null;
  body: string;
  created_at: string;
};

export type ErrorReportOut = {
  id: number;
  user_id?: string | null;
  email?: string | null;
  organization_id?: number | null;
  title: string;
  description: string;
  category: ReportCategory | string;
  status: ReportStatus | string;
  priority: ReportPriority | string;
  assigned_to?: string | null;
  external_ticket_id?: string | null;
  sync_status: string;
  metadata?: ReportMetadata | null;
  app_version?: string | null;
  resolved_at?: string | null;
  created_at: string;
  updated_at?: string | null;
  attachments: AttachmentOut[];
  comments: CommentOut[];
};

export type ErrorReportListItem = {
  id: number;
  user_id?: string | null;
  email?: string | null;
  title: string;
  category: string;
  status: string;
  priority: string;
  created_at: string;
  attachments_count: number;
};

export type ErrorReportListResponse = {
  items: ErrorReportListItem[];
  total: number;
  page: number;
  page_size: number;
};

export type FeedbackMetaOption = { code: string; label: string };

export type FeedbackMetaResponse = {
  categories: FeedbackMetaOption[];
  statuses: FeedbackMetaOption[];
  priorities: FeedbackMetaOption[];
};

export const CATEGORY_LABELS: Record<ReportCategory, string> = {
  bug: "Баг",
  content: "Ошибка в контенте",
  payment: "Ошибка в печати отчетов",
  other: "Другое",
};

export const STATUS_LABELS: Record<ReportStatus, string> = {
  new: "Новый",
  in_progress: "В работе",
  resolved: "Решён",
  closed: "Закрыт",
};

export const PRIORITY_LABELS: Record<ReportPriority, string> = {
  low: "Низкий",
  medium: "Средний",
  high: "Высокий",
  critical: "Критический",
};

/** Автосбор контекста страницы/устройства (без IP — его пишет сервер). */
export function collectReportMetadata(): ReportMetadata {
  return {
    url: window.location.href,
    pathname: window.location.pathname,
    referrer: document.referrer || null,
    user_agent: navigator.userAgent,
    screen: `${window.screen.width}x${window.screen.height}`,
    language: navigator.language || null,
    platform: navigator.platform || null,
  };
}

export const feedbackApi = {
  meta: () => get<FeedbackMetaResponse>("/feedback/meta"),

  createReport: (body: ErrorReportCreatePayload) =>
    post<ErrorReportOut>("/feedback/reports", body),

  getReport: (id: number) => get<ErrorReportOut>(`/feedback/reports/${id}`),

  uploadAttachment: (reportId: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return postFormData<AttachmentOut>(`/feedback/reports/${reportId}/attachments`, fd);
  },

  adminList: (params?: {
    status?: string;
    priority?: string;
    category?: string;
    user_id?: string;
    page?: number;
    page_size?: number;
  }) => {
    const q: Record<string, string | number> = {};
    if (params?.status) q.status = params.status;
    if (params?.priority) q.priority = params.priority;
    if (params?.category) q.category = params.category;
    if (params?.user_id) q.user_id = params.user_id;
    if (params?.page != null) q.page = params.page;
    if (params?.page_size != null) q.page_size = params.page_size;
    return get<ErrorReportListResponse>("/admin/feedback/reports", q);
  },

  adminGet: (id: number) => get<ErrorReportOut>(`/admin/feedback/reports/${id}`),

  adminUpdate: (
    id: number,
    body: { status?: ReportStatus; priority?: ReportPriority; assigned_to?: string | null },
  ) => patch<ErrorReportOut>(`/admin/feedback/reports/${id}`, body),

  adminDelete: (id: number) => del<void>(`/admin/feedback/reports/${id}`),

  adminBulkDelete: (ids: number[]) =>
    post<{ deleted: number }>("/admin/feedback/reports/bulk-delete", { ids }),

  adminAddComment: (id: number, body: string) =>
    post<CommentOut>(`/admin/feedback/reports/${id}/comments`, { body }),

  async adminExportXlsx(params?: {
    status?: string;
    priority?: string;
    category?: string;
    user_id?: string;
  }): Promise<void> {
    const q: Record<string, string> = {};
    if (params?.status) q.status = params.status;
    if (params?.priority) q.priority = params.priority;
    if (params?.category) q.category = params.category;
    if (params?.user_id) q.user_id = params.user_id;
    const blob = await downloadFile("/admin/feedback/reports/export.xlsx", q);
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `feedback-reports-${stamp}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  },
};
